import { observable, computed, action, IObservableArray } from "mobx";

import { AppStore, IWizardStore } from "./AppStore";
import * as DocumentManagement from "../DocumentManagement";
import { CollectionStoreImpl } from "./CollectionStore";

import { DialogStore } from "../Stores/DialogStore";
import { Strings } from "../Stores/StringsStore";



export class CollectionTreeViewStoreBase {

	constructor(public collectionStore: CollectionStoreImpl) { }
	/**
	* The current open collection
	*/
	@observable currentFocusedCollection: string;

	/**
	* Current open tree path
	*/
	treePath: IObservableArray<string> = observable.array([]);

	/**
	* Current root collection tree view model.
	*/
	@observable model: DocumentManagement.CollectionInfo = new DocumentManagement.CollectionInfo();

	@observable currentResult: string;

	/**
	 * Collapses all open collection nodes except the current one.   
	 */
	collapseCollections(parentCollection: DocumentManagement.CollectionInfo) {
		parentCollection.subCollections.forEach(collection => {
			let shouldBeClosed = true;
			if (!this.treePathHasId(collection.guid)) {
				collection.isExpanded = false;
				if (collection.subCollections) {
					collection.subCollections.clear();
				}
			} else {
				this.collapseCollections(collection);
			}
		});
	}

	/**
	* Tree view is in selection mode.
	*/
	@observable inSelectionMode: boolean = false;


	@action toggleSelectionMode = () => {
		this.inSelectionMode = !this.inSelectionMode;
		if (!this.inSelectionMode) {
			this.selectedCollections.clear();
		}
	}

	@observable inMoveMode: boolean = false;


	@action resolveTreePathCollections(parentCollection: DocumentManagement.CollectionInfo, depth: number = 0) {
		if (!this.treePath || this.treePath.length <= depth) return;
		let collection = parentCollection.subCollections.find((subCollection) => subCollection.guid == this.treePath[depth]);
		if (collection) {
			DocumentManagement.getSubCollectionsAsync(collection.guid).then(result => {
				collection.isExpanded = true;
				collection.subCollections = observable.array(result);
				this.resolveTreePathCollections(collection, ++depth);
			});
		}
	}

	getRoots() {
		throw new TypeError("getRoots was not implemented!")
	}

	/**
	* Resolves the child collection for the given collection.
	*/
	@action getSubCollection = (collection: DocumentManagement.CollectionInfo) => {
		DocumentManagement.getSubCollectionsAsync(collection.guid).then(result => {
			collection.subCollections = observable.array(result);
			if (!this.inSelectionMode) {
				this.clearTreePath(collection.parentId);
				this.pushTreeNodeToHistory(collection.guid);
				this.collapseCollections(this.model);
				if (!this.inMoveMode) {
					this.pushCollectionTreePathHistory();
				}
			}

		}).catch(error => DialogStore.showError(Strings.general.errorDialogTitle, error));
	}

	/**
	 * All current selected collection items
	 */
	selectedCollections: IObservableArray<DocumentManagement.CollectionInfo> = observable<DocumentManagement.CollectionInfo>([]);

	/**
	 * Adds a collection to the selection list for moving or deleting operation
	 * @param collectionItem The selected collection item
	 */
	@action addCollectionToSelection(collectionItem: DocumentManagement.CollectionInfo): void {
		DocumentManagement.getCollectionHierarchyAsync(collectionItem.guid).then(result => {
			let currentParents: string[] = result.parentGuids,
				currentChildren: string[] = result.childGuids;
			let existingItems = this.selectedCollections.filter(c => c.guid === collectionItem.guid);
			if (existingItems.length === 0) {
				this.removeSelectedItems(currentParents);
				this.removeSelectedItems(currentChildren);
				this.selectedCollections.push(collectionItem);
			}
			else {
				this.selectedCollections.remove(existingItems[0]);
			}
		});
	}

	@action private removeSelectedItems(items: string[]) {
		if (items.length == 0) return;
		items.forEach(itemGuid => {
			let selectedItem = this.selectedCollections.find(selectedCollection => selectedCollection.guid === itemGuid);
			if (selectedItem) {
				this.selectedCollections.remove(selectedItem);
			}
		});
	}

	/**
	 * Pushes the current tree path to the history.
	 */
	pushCollectionTreePathHistory() {
		AppStore.history.replace({ pathname: window.location.hash.split("?")[0].replace("#", ""), search: "?treepath=" + this.treePath.filter(function (e) { return e }).join(",") });
	}

	@action pushTreeNodeToHistory(nodeID: string) {
		this.treePath.push(nodeID);
	}

	@action popTreeNodeFromPath(nodeID: string) {
		let index = this.findPathParentIndex(nodeID);
		this.treePath.replace(this.treePath.slice(0, index > 0 ? index : 0));
	}

	@action clearTreePath(parentID: string) {
		let parentIndex = this.findPathParentIndex(parentID);
		if (!(parentIndex > -1)) {
			this.treePath.replace(this.treePath.slice(0, 0));
		}
		else {
			this.treePath.replace(this.treePath.slice(0, parentIndex + 1));
		}
	}

	treePathHasId(parentID: string): boolean {
		return this.findPathParentIndex(parentID) > - 1;
	}

	findPathParentIndex(parentID: string): number {
		let index = -1;
		for (let i = this.treePath.length - 1; i >= 0; i--) {
			if (this.treePath[i] === parentID) {
				index = i;
				break;
			}
		}
		return index;
	}

}